import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import CustomPagination from '@components/Common/table/CustomPagination';
import { useModalDisclosure } from '@hooks/useDisclosure';
import { failed, success } from '@components/Common/Toastify';
import { defaultParams } from '@utils/global.constants';
import {
  RefundRequestDto,
  useRefundRequestsQuery,
  useResolveRefundRequestMutation,
} from '../../API/Subscriptions';

const RefundRequests = () => {
  const [params, setParams] = useState({
    ...defaultParams,
    page: 1,
  });

  const { data, isLoading, isFetching, error, refetch } = useRefundRequestsQuery({
    params: { page: params.page, limit: params.pageSize },
    config: { keepPreviousData: true },
  });

  const requests: RefundRequestDto[] = useMemo(() => data?.data || [], [data?.data]);
  const meta = data?.meta;

  const resolveModal = useModalDisclosure();
  const [resolveAction, setResolveAction] = useState<'approve' | 'reject'>('approve');
  const [adminComment, setAdminComment] = useState<string>('');

  const resolveMutation = useResolveRefundRequestMutation({
    onSuccess: () => {
      success(`Refund request ${resolveAction}d successfully`);
      refetch();
      resolveModal.close();
      setAdminComment('');
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || `Failed to ${resolveAction} refund request`);
    },
  });

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const openResolve = (req: RefundRequestDto, action: 'approve' | 'reject') => {
    setResolveAction(action);
    setAdminComment('');
    resolveModal.open(req);
  };

  const submitResolve = () => {
    const req = resolveModal.data as RefundRequestDto;
    if (!req?.id) return;

    resolveMutation.mutate({
      id: req.id,
      action: resolveAction,
      comment: adminComment.trim() || undefined,
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(amount / 100);
    } catch {
      return `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`;
    }
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'refunded':
        return 'success';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'rejected':
        return 'error';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load refund requests.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2>Refund Requests</h2>
        </div>

        <TableContainer component={Paper}>
          {isLoading || isFetching ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading refund requests...</Typography>
            </Box>
          ) : requests.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No refund requests found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Subscription ID</TableCell>
                  <TableCell>Payment Reference</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Admin Comment</TableCell>
                  <TableCell>Requested Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => {
                  const userLabel = req.user ? `${req.user.email} (#${req.user.id})` : `User #${req.userId}`;
                  return (
                    <TableRow key={req.id}>
                      <TableCell>{userLabel}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {req.stripeSubscriptionId}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        <div><strong>Intent:</strong> {req.stripePaymentIntentId}</div>
                        <div><strong>Charge:</strong> {req.stripeChargeId}</div>
                        {req.stripeRefundId && <div><strong>Refund ID:</strong> {req.stripeRefundId}</div>}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatAmount(req.amount, req.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status}
                          color={getStatusChipColor(req.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                        {req.reason}
                      </TableCell>
                      <TableCell sx={{ maxWidth: '200px', wordBreak: 'break-word', color: 'text.secondary' }}>
                        {req.adminComment || '-'}
                      </TableCell>
                      <TableCell>
                        {req.createdAt ? dayjs(req.createdAt).format('MMM DD, YYYY HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        {(req.status === 'pending' || req.status === 'failed') ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => openResolve(req, 'approve')}
                              disabled={resolveMutation.isLoading}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => openResolve(req, 'reject')}
                              disabled={resolveMutation.isLoading}
                            >
                              Reject
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {req.resolvedAt ? dayjs(req.resolvedAt).format('MMM DD, YYYY') : '-'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!isLoading && requests.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <CustomPagination
              totalPages={meta?.totalPages || 1}
              page={meta?.currentPage || params.page}
              handlePage={handlePageChange}
            />
          </Box>
        )}

        {/* Resolve request dialog */}
        <Dialog open={resolveModal.isOpen} onClose={resolveModal.close} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textTransform: 'capitalize' }}>
            {resolveAction} Refund Request
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Are you sure you want to <strong>{resolveAction}</strong> this refund request? 
                {resolveAction === 'approve' && ' This will immediately refund the payment via Stripe and terminate their subscription.'}
              </Typography>
              <TextField
                label="Admin Comment"
                placeholder="Provide a reason or comment..."
                multiline
                rows={3}
                fullWidth
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resolveModal.close} disabled={resolveMutation.isLoading}>
              Cancel
            </Button>
            <Button
              onClick={submitResolve}
              variant="contained"
              color={resolveAction === 'approve' ? 'success' : 'error'}
              disabled={resolveMutation.isLoading}
            >
              {resolveAction === 'approve' ? 'Approve & Refund' : 'Reject Request'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default RefundRequests;
